import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { FilterRecord, MyArvatoEnvironment } from '@myarvato/shared/models';
import { KpiCardDefinition, KpiDto, KpiResultDto } from '@myarvato/components/kpi';
import { FilterMetadata } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { Observable, of } from 'rxjs';
import { KpiCardProviderService } from './kpi-card-provider.service';

@Injectable({
  providedIn: 'root',
})
export class KpiService {
  // injections
  private readonly environment = inject(MyArvatoEnvironment);
  private readonly httpClient = inject(HttpClient);
  private readonly kpiCardProviderService = inject(KpiCardProviderService);

  // fields
  private readonly baseUrl = `${this.environment.url.api}Transport/KPI`;

  // Fallback default KPIs (used if no definition is marked as default)
  private readonly fallbackDefaults = ['Total', 'Delayed', 'Exception'];

  // State Management
  public readonly kpiData = signal<KpiDto[]>([]);
  public readonly isLoading = signal<boolean>(false);
  public readonly allCardDefinitions = signal<KpiCardDefinition[]>(this.kpiCardProviderService.GetKpiCardDefinitions());

  public readonly cardDefinitionDefaults = computed(() => {
    const cardDefinitions = this.allCardDefinitions()
      .filter((definition) => definition.isDefault)
      .map((definition) => definition.field);
    return cardDefinitions.length > 0 ? cardDefinitions : this.fallbackDefaults;
  });

  public readonly visibleKpiFields = signal<string[]>(this.cardDefinitionDefaults());

  public readonly cardDefinitions = computed(() => {
    const visible = this.visibleKpiFields();
    const allDefinitions = this.allCardDefinitions();

    return visible
      .map((field) => allDefinitions.find((definition) => definition.field === field))
      .filter((definition): definition is KpiCardDefinition => !!definition);
  });

  public getAllKpis(): KpiCardDefinition[] {
    return this.allCardDefinitions();
  }

  /**
   * Retrieves KPI data based on the provided filter.
   * @param filter - The filter criteria for loading KPI data, typically used for lazy loading.
   * @returns An observable emitting the KPI data (`KpiDto`).
   */
  public getKpis(filter: TableLazyLoadEvent): Observable<KpiResultDto> {
    if (filter.last === 0) {
      return of({ lastUpdate: null!, kpis: null! });
    }

    return this.httpClient.post<KpiResultDto>(`${this.baseUrl}`, filter, this.getRequestOptions());
  }

  public getVisibleKpiFields(): string[] {
    return this.visibleKpiFields();
  }

  public readonly kpiOrder = signal<string[]>(this.allCardDefinitions().map((d) => d.field));

  public getKpiOrder(): string[] {
    return this.kpiOrder();
  }

  // Reorder ALL fields
  public setKpiOrder(fields: string[]): void {
    this.kpiOrder.set([...fields]);
  }

  public setVisibleKpiFields(fields: string[]): void {
    this.visibleKpiFields.set(fields);
  }

  /**
   * Restores the default visible KPIs (e.g., after clicking "Reset View").
   * Also clears current KPI data to hide UI elements like the Manage KPI button.
   */
  public resetToDefaultKpis(): void {
    const defaults = this.cardDefinitionDefaults();
    this.visibleKpiFields.set([...defaults]);

    const defaultOrder = this.allCardDefinitions().map((definition) => definition.field);
    this.kpiOrder.set([...defaultOrder]);
  }

  public loadKpis(filter: TableLazyLoadEvent): void {
    if ((filter.first ?? 0) === 0) {
      this.isLoading.set(true);

      this.getKpis(filter).subscribe({
        next: (data) => {
          this.kpiData.set(data.kpis);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error fetching KPI data:', err);
          this.kpiData.set([]);
          this.isLoading.set(false);
        },
      });
    }
  }

  //Computed subset of KPI definitions currently visible on the UI.
  public readonly filteredCardDefinitions = computed(() => {
    const visible = this.visibleKpiFields();
    const allDefinitions = this.allCardDefinitions();
    const order = this.kpiOrder();

    // Only visible KPIs
    const visibleDefinitions = allDefinitions.filter((definition) => visible.includes(definition.field));

    // Sort by global order
    return [...visibleDefinitions].sort((a, b) => {
      const ia = order.indexOf(a.field);
      const ib = order.indexOf(b.field);
      return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
    });
  });

  public handleKpiCardClick(
    kpiCard: KpiCardDefinition,
    currentFilters?: FilterRecord,
  ): {
    filters: FilterRecord;
    columnsToAdd: string[];
    shouldRemoveFilters: boolean;
    filterFieldsToRemove: string[];
  } {
    return kpiCard.active ? this.handleDeactivateKpiCard(kpiCard, currentFilters) : this.handleActivateKpiCard(kpiCard, currentFilters);
  }

  private handleDeactivateKpiCard(
    kpiCard: KpiCardDefinition,
    currentFilters?: FilterRecord,
  ): {
    filters: FilterRecord;
    columnsToAdd: string[];
    shouldRemoveFilters: boolean;
    filterFieldsToRemove: string[];
  } {
    const updatedFilters: FilterRecord = { ...currentFilters };
    const kpiFilters = kpiCard.filters || {};
    const filterFieldsToRemove: string[] = [];

    Object.entries(kpiFilters).forEach(([field, filterArray]) => {
      const hasValidFilterArray = filterArray && filterArray.length > 0;
      const currentFieldFilter = updatedFilters[field];
      const shouldProcessField = hasValidFilterArray && Array.isArray(currentFieldFilter);
      const remainingValues =
        shouldProcessField && filterArray ? this.removeKpiValuesFromFilter(filterArray[0].value, currentFieldFilter) : [];

      const shouldKeepField = remainingValues.length > 0;
      if (shouldKeepField) {
        this.updateFilterValues(updatedFilters[field], remainingValues);
      } else {
        delete updatedFilters[field];
        filterFieldsToRemove.push(field);
      }
    });

    return {
      filters: updatedFilters,
      columnsToAdd: [],
      shouldRemoveFilters: true,
      filterFieldsToRemove,
    };
  }

  private handleActivateKpiCard(
    kpiCard: KpiCardDefinition,
    currentFilters?: FilterRecord,
  ): {
    filters: FilterRecord;
    columnsToAdd: string[];
    shouldRemoveFilters: boolean;
    filterFieldsToRemove: string[];
  } {
    const selectedKpiFilters = kpiCard.filters || {};
    const columnsToAdd = Object.keys(selectedKpiFilters);
    const safeFilters: FilterRecord = { ...currentFilters };

    Object.entries(selectedKpiFilters).forEach(([key, value]) => {
      const hasValidValue = value && value.length > 0;
      const kpiFilterValue = hasValidValue ? value[0].value : null;
      const existingFilter = safeFilters[key];

      if (hasValidValue && value) {
        safeFilters[key] = this.mergeOrReplaceFilter(existingFilter, value, kpiFilterValue);
      }
    });

    return {
      filters: safeFilters,
      columnsToAdd,
      shouldRemoveFilters: false,
      filterFieldsToRemove: [],
    };
  }

  private removeKpiValuesFromFilter(kpiFilterValue: unknown, currentFieldFilter: FilterMetadata[]): unknown[] {
    const inFilter = currentFieldFilter.find((f) => f.matchMode === 'in');
    const canRemoveValues = inFilter && Array.isArray(inFilter.value) && Array.isArray(kpiFilterValue);

    return canRemoveValues ? inFilter.value.filter((v: unknown) => !kpiFilterValue.includes(v)) : [];
  }

  private updateFilterValues(fieldFilter: FilterMetadata[], remainingValues: unknown[]): void {
    const inFilter = fieldFilter.find((f) => f.matchMode === 'in');
    const canUpdate = inFilter && Array.isArray(inFilter.value);

    if (canUpdate) {
      inFilter.value = remainingValues;
    }
  }

  private mergeOrReplaceFilter(
    existingFilter: FilterMetadata[] | undefined,
    newValue: FilterMetadata[],
    kpiFilterValue: unknown,
  ): FilterMetadata[] {
    return existingFilter ? this.mergeWithExistingFilter(existingFilter, newValue, kpiFilterValue) : newValue;
  }

  private mergeWithExistingFilter(existingFilter: FilterMetadata[], newValue: FilterMetadata[], kpiFilterValue: unknown): FilterMetadata[] {
    const existingInFilter = existingFilter.find((f) => f.matchMode === 'in');
    const canMerge = existingInFilter && Array.isArray(existingInFilter.value) && Array.isArray(kpiFilterValue);

    return canMerge ? this.createMergedFilter(existingFilter, existingInFilter, kpiFilterValue) : newValue;
  }

  private createMergedFilter(
    existingFilter: FilterMetadata[],
    existingInFilter: FilterMetadata,
    kpiFilterValue: unknown[],
  ): FilterMetadata[] {
    existingInFilter.value = [...new Set([...existingInFilter.value, ...kpiFilterValue])];
    return existingFilter;
  }

  private getRequestOptions() {
    const headers = new HttpHeaders({
      'x-skip-all-interceptor-handling': 'true',
      'x-skip-loading-interceptor-handling': 'true',
      'x-error-message-target-key': 'none',
    });

    return { headers: headers };
  }
}
